import type { SearchConfig } from "../types";

/**
 * Seed data: the reviewed source of `workers-compensation-search-config.json`
 * in each stage's configs bucket. The page renders the stored document, not
 * this, so a change here reaches users only once the stored document is updated
 * to match. See `SearchConfigRegistryEntry.searchConfig`.
 */
export const WC_SEARCH_CONFIG: SearchConfig = {
  lob: "wc",
  claimType: "workers-compensation",
  retrievalPageSize: 100,
  testId: "wc-claims-search-results",

  // No emptyPlaceholder on any column: the cells this replaces render the raw
  // property, so an absent value renders nothing.

  // Every `queryName` below matches `workers-compensation-query-config.json` as
  // deployed to alpha, beta and gamma, which hold byte-identical copies. Prod is
  // unverified — confirm there before enabling the flag, since a name the
  // manifest does not define fails at the service rather than at build or
  // validation time.
  searchableFields: [
    {
      fieldId: "alias",
      queryName: "byAssociateAlias",
      queryInputProperty: "associateAlias",
      maxTermLength: 11,
      normalization: "lowercase",
      labels: {
        buttonText: "Search by Alias/Login",
        buttonTextSmall: "By Alias",
        placeholder: "Search for claims using the injured employee's alias/login",
        placeholderSmall: "Search using alias/login",
      },
      // The search term itself is the counter name, which is what the current
      // publisher does: publishClaimsSearchByAlias(alias) counts under `alias`.
      metrics: [{ publisherName: "claimsSearchByAlias", counterName: { kind: "searchTerm" } }],
    },
    {
      fieldId: "employeeId",
      queryName: "byAssociateEmployeeId",
      // `associateEmployeeId`, not the legacy input's `associateEmployeeID`.
      queryInputProperty: "associateEmployeeId",
      maxTermLength: 11,
      validationRules: [{ ruleId: "digitsOnly", message: "Employee ID must be only numbers." }],
      labels: {
        buttonText: "Search by Employee ID",
        buttonTextSmall: "By EID",
        placeholder: "Search for claims using the injured employee's ID",
        placeholderSmall: "Search using Employee ID",
      },
      metrics: [{ publisherName: "claimsSearchByEmployeeId", counterName: { kind: "searchTerm" } }],
    },
    {
      fieldId: "site",
      queryName: "byIncidentSiteName",
      // `incidentSiteName`, not the legacy input's `incidentSite`.
      queryInputProperty: "incidentSiteName",
      maxTermLength: 11,
      normalization: "uppercase",
      acceptsDateRange: true,
      labels: {
        buttonText: "Search by Incident Site",
        buttonTextSmall: "By Site",
        placeholder: "Search for claims using the Incident Site",
        placeholderSmall: "Search using Site",
      },
      metrics: [{ publisherName: "claimsSearchBySite", counterName: { kind: "searchTerm" } }],
    },
  ],

  // Bounds bind to the range key of `incidentSiteName-createdAtTimestamp-gsi`,
  // which is why only the `site` field sets `acceptsDateRange`. These names must
  // match the manifest's `rangeCondition`: a mismatch drops the bounds
  // server-side and returns an unbounded result set rather than erroring.
  dateRangeRequest: {
    startQueryInputProperty: "submittedStartDate",
    endQueryInputProperty: "submittedEndDate",
  },

  // `countryFilter` stays absent: no WC index projects `country`, so filtering on
  // it matches no row and renders an empty table with a 200. FT-LYRA-11115 owns
  // the projection widening and attribute backfill it needs first.
  table: {
    columns: [
      {
        columnId: "site",
        header: "Incident Site",
        source: "incidentSiteName",
        sorting: { kind: "field", sortingField: "incidentSiteName" },
      },
      {
        columnId: "claimNumber",
        header: "Claim Number",
        source: "claimNumber",
        sorting: { kind: "field", sortingField: "claimNumber" },
        width: 180,
      },
      {
        columnId: "employeeLogin",
        header: "Employee Login",
        source: "associateAlias",
        sorting: { kind: "field", sortingField: "associateAlias" },
      },
      {
        columnId: "employeeID",
        header: "Employee ID",
        source: "associateEmployeeId",
        sorting: { kind: "field", sortingField: "associateEmployeeId" },
      },
      {
        columnId: "incidentDate",
        header: "Incident Date",
        source: "incidentTimestamp",
        sorting: { kind: "field", sortingField: "incidentTimestamp" },
        derivation: { kind: "epochSecondsToDate", timeZone: "viewerLocal" },
      },
      {
        columnId: "createdAtDate",
        header: "Submitted Date",
        source: "createdAtTimestamp",
        sorting: { kind: "field", sortingField: "createdAtTimestamp" },
        derivation: { kind: "epochSecondsToDate", timeZone: "viewerLocal" },
      },
      {
        columnId: "submitterLogin",
        header: "Submitter Login",
        source: "claimReporterAlias",
        sorting: { kind: "field", sortingField: "claimReporterAlias" },
      },
      {
        columnId: "bodyPart",
        header: "Body Part",
        source: "primaryInjuredBodyPartDescription",
        sorting: { kind: "comparator", comparator: "derivedDisplayValue" },
        derivation: {
          kind: "concatenate",
          separator: " ",
          parts: [
            { source: "primaryInjuredBodyPartDescription", required: true },
            { source: "secondaryInjuredBodyPartDescription", required: false },
          ],
        },
      },
    ],

    defaultSorting: { columnId: "createdAtDate", direction: "descending" },

    pageSize: 10,
    minItemsForControls: 5,
    loadingText: "Loading Claims",
    resourceName: "Claim",
    resultsHeaderText: "Search Results",
    filterPlaceholder: "Filter claims",
    filterAriaLabel: "Filter claims",
    // Split in two: the "more claims" note now applies to every search, and the
    // date-range advice is appended only where a date range was selected.
    // Together they read as today's single sentence did.
    moreResultsSuffix: " (More claims available)",
    narrowDateRangeSuffix: " Narrow down the date range to load all claims.",
  },

  exportConfig: {
    fileNamePrefix: "wc_claims_search_results",
    fields: [
      { label: "Incident Site", source: "incidentSiteName" },
      { label: "Claim Number", source: "claimNumber" },
      { label: "Employee Login", source: "associateAlias" },
      { label: "Employee ID", source: "associateEmployeeId" },
      {
        label: "Incident Date",
        source: "incidentTimestamp",
        derivation: { kind: "epochSecondsToDate", timeZone: "viewerLocal" },
      },
      {
        label: "Submitted Date",
        source: "createdAtTimestamp",
        derivation: { kind: "epochSecondsToDate", timeZone: "viewerLocal" },
      },
      { label: "Submitter Login", source: "claimReporterAlias" },
      {
        label: "Body Part",
        source: "primaryInjuredBodyPartDescription",
        derivation: {
          kind: "concatenate",
          separator: " ",
          parts: [
            { source: "primaryInjuredBodyPartDescription", required: true },
            { source: "secondaryInjuredBodyPartDescription", required: false },
          ],
        },
      },
    ],
  },

  mySubmittedClaims: {
    queryName: "byClaimReporterAlias",
    queryInputProperty: "claimReporterAlias",
    pageSize: 100,
    tabLabel: "My Submitted Claims",
    resultsHeaderText: "My Submitted Claims",
    limitReachedSuffix: " (Most Recent 100)",
    exportFileNamePrefix: "my-submitted-claims",
    metrics: [
      {
        publisherName: "mySubmittedClaims",
        counterName: { kind: "literal", value: "mySubmittedClaimsVisited" },
      },
      { publisherName: "mySubmittedClaimsVisitedBySite", counterName: { kind: "userSiteCode" } },
    ],
  },

  metrics: {
    configRetrievalPublisherName: "claimsHistory.resolveSearchConfig",
    pageVisited: [
      {
        publisherName: "searchResultsVisited",
        counterName: { kind: "literal", value: "searchResultsVisited" },
      },
      { publisherName: "searchResultsVisitedBySite", counterName: { kind: "userSiteCode" } },
    ],
    searchSubmitted: [
      {
        publisherName: "claimsSearch",
        counterName: { kind: "literal", value: "claimsSearchBarUsed" },
      },
      { publisherName: "claimsSearchBarUsedBySite", counterName: { kind: "userSiteCode" } },
    ],
  },
};
